import { LitElement, PropertyValueMap, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { withAuth } from '@amityeko/pbc-core/dist/providers/auth/consumer-mixin';

import awardIcon from '@amityeko/design-system/dist/assets/icons/award';

import '@amityeko/design-system/dist/elements/typography/tg-headline';
import '@amityeko/design-system/dist/elements/typography/tg-body-two';
import '@amityeko/design-system/dist/elements/icon';
import '@amityeko/design-system/dist/elements/dialog';
import '@amityeko/design-system/dist/components/empty-view';
import type AmityekoDialog from '@amityeko/design-system/dist/elements/dialog';

import '../card-list';

import type { RecognitionSummary } from './types';

import styles from './styles';

@customElement('rnr-recognition-list')
export default class RnRRecognitionList extends withAuth(LitElement) {
  static styles = styles;

  @query('#dialog') dialog!: AmityekoDialog;

  @property()
  receiverId?: string;

  @state()
  recognitionSummary?: RecognitionSummary;

  @state()
  limit: number = 9;

  public show() {
    this.dialog.show();
  }

  protected updated(changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>) {
    if (!changedProperties.has('auth') || !this.auth) return;

    const prevAuth: typeof this.auth = changedProperties.get('auth');
    if (this.auth.isAuthenticated && !prevAuth?.isAuthenticated) {
      this._getRecognitionSummary();
    }
  }

  private async _getRecognitionSummary() {
    if (!this.auth?.http || !this.receiverId) return;

    try {
      // call summary api
      // TODO: To add other parameters
      this.recognitionSummary = await this.auth.http.get(
        '/api/v1/recognitions/achievements/summary',
        { receiverId: this.receiverId },
      );
    } catch (e: any) {
      emit(this, 'amityeko-notification--toast', {
        detail: { message: e?.error?.message, variant: 'error' },
      });
    }
  }

  private _onClickBack() {
    this.dialog.hide();
  }

  private _onSelectCard(e: CustomEvent) {
    emit(this, 'rnr-recognition-list-select-card', {
      detail: { data: e?.detail },
    });
    this.dialog.hide();
  }

  protected render() {
    return html`
      <amityeko-dialog
        id="dialog"
        .label=${'Achievements'}
        ?hasBackButton=${true}
        @amityeko-back=${this._onClickBack}
      >
        <div class="content">
          ${this.recognitionSummary
            ? html`
                <div class="card-summary">
                  <div class="card-summary-content">
                    <amityeko-tg-headline>
                      ${this.recognitionSummary?.totalBadges}
                    </amityeko-tg-headline>
                    <amityeko-tg-body-two>Achieved Badges</amityeko-tg-body-two>
                  </div>
                  <div class="card-summary-content">
                    <amityeko-tg-headline>
                      ${this.recognitionSummary?.totalPoints}
                    </amityeko-tg-headline>
                    <amityeko-tg-body-two>Total Points</amityeko-tg-body-two>
                  </div>
                </div>
                <div class="cards">
                  <rnr-client-badge-card-list
                    .limit=${this.limit}
                    .receiverId=${this.receiverId}
                    @rnr-select-card=${this._onSelectCard}
                  ></rnr-client-badge-card-list>
                </div>
              `
            : html`
                <amityeko-empty-view
                  label="No Achievements here"
                  caption="Achievements may be available soon!"
                >
                  <amityeko-icon slot="badge-icon">${awardIcon}</amityeko-icon>
                </amityeko-empty-view>
              `}
        </div>
      </amityeko-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-recognition-list': RnRRecognitionList;
  }
}
